const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;

void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `
precision highp float;

varying vec2 v_uv;

uniform vec2 u_resolution;
uniform float u_time;

float hash(vec2 p) {
  p = fract(p * vec2(443.8975, 397.2973));
  p += dot(p, p.yx + 19.19);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);

  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));

  vec2 u = f * f * (3.0 - 2.0 * f);

  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 5; i++) {
    value += amplitude * noise(p);
    p = p * 2.02 + vec2(31.34, 17.81);
    amplitude *= 0.5;
  }

  return value;
}

float aaWidth() {
  return 1.25 / min(u_resolution.x, u_resolution.y);
}

float ramp(float x, float a, float b) {
  return smoothstep(a, b, x);
}

float leftToRightAmplitude(float x) {
  return mix(0.12, 1.0, ramp(x, 0.30, 0.95));
}

float leftToRightNoise(float x) {
  return mix(0.08, 1.0, ramp(x, 0.36, 0.98));
}

float terrainWave(float x, float phase) {
  return
    sin(x * 3.7 + phase) * 0.55 +
    sin(x * 7.9 + phase * 0.73) * 0.28 +
    sin(x * 14.6 - phase * 0.35) * 0.17;
}

float octaveNoise(float x, float y, float phase) {
  vec2 base = vec2(x, y);
  float n1 = fbm(base * vec2(0.85, 1.25) + vec2(phase * 0.13, 0.0));
  float n2 = fbm(base * vec2(1.9, 2.4) + vec2(12.4 + phase * 0.19, 4.7));
  float n3 = fbm(base * vec2(4.7, 5.8) + vec2(28.1 - phase * 0.11, 9.3));
  return (n1 * 0.58 + n2 * 0.28 + n3 * 0.14) - 0.5;
}

float boundaryY(float x, float baseY, float amplitude, float phase, float noiseAmount) {
  float ampRamp = leftToRightAmplitude(x);
  float noiseRamp = leftToRightNoise(x);
  float wave = terrainWave(x, phase);
  float noisy = octaveNoise(x, baseY * 6.0, phase) * noiseAmount * noiseRamp;
  return baseY + wave * amplitude * ampRamp + noisy;
}

float sdfBelow(float y, float boundary) {
  return y - boundary;
}

float fillFromSdf(float sdf, float feather) {
  return 1.0 - smoothstep(-feather, feather, sdf);
}

float strokeFromSdf(float sdf, float thickness, float feather) {
  float edge = abs(sdf) - thickness;
  return 1.0 - smoothstep(-feather, feather, edge);
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  float x = uv.x * aspect;
  float y = uv.y + 0.75 * (1. -  0.25 * uv.x);
  float feather = aaWidth();

  vec3 darkGreen = vec3(0.341176, 0.427451, 0.329412);
  vec3 lightGreen = vec3(0.560784, 0.643137, 0.549020);
  vec3 tan = vec3(0.949020, 0.878431, 0.788235);
  vec3 white = vec3(0.988235, 0.992157, 0.984314);

  vec3 color = darkGreen;

  float greenBoundary = boundaryY(x, 0.785, 0.042, 0.55, 0.020);
  float tanBoundary = boundaryY(x, 0.900, 0.034, 1.35, 0.016);
  float lineBoundary = boundaryY(x, 0.796, 0.024, 0.88, 0.010);

  float greenFill = fillFromSdf(sdfBelow(y, greenBoundary), feather);
  float tanFill = fillFromSdf(sdfBelow(y, tanBoundary), feather);

  color = mix(color, lightGreen, greenFill);
  color = mix(color, tan, tanFill);

  float lineStroke = strokeFromSdf(y - lineBoundary, 0.0065, feather * 1.35);
  float lineShadow = strokeFromSdf(y - (lineBoundary + 0.011), 0.011, feather * 1.6);

  color = mix(color, darkGreen * 0.92, lineShadow * 0.18);
  color = mix(color, white, lineStroke);

  float lowerShade = smoothstep(0.72, 1.0, y) * 0.05;
  color *= 1.0 - lowerShade;

  gl_FragColor = vec4(color, 1.0);
}
`;

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error('Unable to create shader.');
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Shader compile failed.';
    gl.deleteShader(shader);
    throw new Error(message);
  }

  return shader;
}

function createProgram(gl) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE);
  const program = gl.createProgram();

  if (!program) {
    throw new Error('Unable to create WebGL program.');
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'Program link failed.';
    gl.deleteProgram(program);
    throw new Error(message);
  }

  return program;
}

function mountHomepageHeaderShader() {
  if (!document.body.classList.contains('homepage-glass')) return;

  const header = document.querySelector('#header-component');
  if (!header || header.querySelector('.homepage-header-shader')) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'homepage-header-shader';
  canvas.setAttribute('aria-hidden', 'true');
  header.prepend(canvas);

  const gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: true,
    depth: false,
    powerPreference: 'high-performance',
    premultipliedAlpha: false,
    stencil: false,
  });

  if (!gl) {
    header.dataset.homepageHeaderShader = 'fallback';
    return;
  }

  let animationFrameId = 0;
  let destroyed = false;

  try {
    const program = createProgram(gl);
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const timeLocation = gl.getUniformLocation(program, 'u_time');

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
        1, -1,
        -1, 1,
        -1, 1,
        1, -1,
        1, 1,
      ]),
      gl.STATIC_DRAW
    );

    gl.useProgram(program);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const resize = () => {
      const rect = header.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(width * devicePixelRatio);
      canvas.height = Math.round(height * devicePixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(header);
    resize();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const startTime = performance.now();

    const render = (now) => {
      if (destroyed) return;

      const elapsed = prefersReducedMotion.matches ? 0 : (now - startTime) * 0.001;

      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }

      if (timeLocation) {
        gl.uniform1f(timeLocation, elapsed);
      }
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animationFrameId = window.requestAnimationFrame(render);
    };

    animationFrameId = window.requestAnimationFrame(render);

    const cleanup = () => {
      destroyed = true;
      resizeObserver.disconnect();
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };

    window.addEventListener('pagehide', cleanup, { once: true });
  } catch (error) {
    console.error('Homepage header shader failed to initialize.', error);
    header.dataset.homepageHeaderShader = 'fallback';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountHomepageHeaderShader, { once: true });
} else {
  mountHomepageHeaderShader();
}
